<script setup>
import {onMounted, onUnmounted, ref} from "vue";
import {deleteUser, getUsers} from "@/services/userService";
import router from "@/router";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import {trackScreenSize} from "@/globals/utils/getScreenSize";
import {formatDate} from "@/globals/utils/formatDate";
import MailTo from "../../../utils/MailTo.vue";
import PhoneCall from "@/components/utils/PhoneCall.vue";
import {customToast} from "@/globals/utils/customToast";

const users = ref([]);
const loading = ref(true);
const error = ref(null);
const screenInfo = ref({width: 0, height: 0, deviceType: "DESKTOP"});
let removeListener;

const fetchUsers = async () => {
  loading.value = true;
  try {
    users.value = await getUsers();
  } catch (err) {
    error.value = "Error al cargar los usuarios.";
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const deleteUserHandler = async (id) => {
  if (confirm("¿Estás seguro de que deseas eliminar este usuario?")) {
    try {
      await deleteUser(id);
      users.value = users.value.filter((user) => user._id !== id);
      customToast("Usuario eliminado correctamente.", "success");
    } catch (err) {
      customToast("Error al eliminar el usuario.", "error");
    }
  }
};

const editUser = (user) => {
  router.push({name: "EditUser", params: {id: user._id}});
};

onMounted(() => {
  fetchUsers();
  removeListener = trackScreenSize((info) => {
    screenInfo.value = info;
  });
});

onUnmounted(() => {
  if (removeListener) removeListener();
});
</script>

<template>
  <LoadingComponent v-if="loading" />
  <div v-else class="overflow-x-auto bg-white dark:bg-gray-700 rounded-lg shadow pb-3">
    <table class="min-w-full table-auto">
      <thead class="bg-gray-100 dark:bg-gray-700">
      <tr>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Name</th>
        <th
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Email
        </th>
        <th
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Phone
        </th>
        <th
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Role
        </th>
        <th
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Created At
        </th>
        <th
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Updated At
        </th>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr
          v-for="user in users"
          :key="user.id"
          class="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200 dark:odd:bg-gray-700 dark:even:bg-gray-600 dark:hover:bg-gray-500"
      >
        <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ user.name }}</td>
        <td
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
        >
          <MailTo :email="user.email" v-if="user.email" />
          <span v-else>Without email</span>
        </td>
        <td
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
        >
          <PhoneCall :phone="user.phone" v-if="user.phone" />
          <span v-else>Without phone</span>
        </td>
        <td
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
        >
          {{ user.role }}
        </td>
        <td
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
        >
          {{ formatDate(user.createdAt) }}
        </td>
        <td
            v-if="screenInfo.deviceType === 'DESKTOP'"
            class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
        >
          {{ formatDate(user.updatedAt) }}
        </td>
        <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          <div class="flex items-center space-x-2">
            <button
                @click="editUser(user)"
                class="flex items-center justify-center px-2 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-sm font-medium rounded"
            >
              <span class="material-icons-round" style="font-size: 16px;">edit</span>
            </button>
            <button
                @click="deleteUserHandler(user._id)"
                class="flex items-center justify-center px-2 py-1 bg-red-300 hover:bg-red-400 dark:bg-red-700 dark:hover:bg-red-600 text-sm font-medium rounded"
            >
              <span class="material-icons-round" style="font-size: 16px;">delete</span>
            </button>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
/* Puedes agregar estilos adicionales según sea necesario */
</style>

<style scoped>
/* Añadir estilos específicos si es necesario */
</style>
